import { DocumentationIcon, ForwardIcon, SearchIcon } from '@assets/icons';
import { TextInput } from '@components/form';
import { Group } from '@components/layout';
import { ExternalLink } from '@components/link';
import { TreeItem, TreeView } from '@components/tree';
import { Typography } from '@components/typography';
import { useSearch } from '@hooks/UseSearch';
import { Box, Popover, Stack, useTheme } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { ChannelDetailContext, GraphDataContext, ZoomContext } from '@subsets/workspaces';
import { ChannelNode } from '@subsets/workspaces/graphs/graph/types';
import { useContext, useState } from 'react';

const ChannelNodeTypeViewLabel = ({
  nodeType,
  categoryColor,
  arrow,
}: {
  nodeType: ChannelNode;
  categoryColor: string;
  arrow: boolean;
}) => {
  const { palette } = useTheme();
  const { name, alias } = nodeType;
  const { createNode } = useContext(GraphDataContext);
  const { getCenterPosition } = useContext(ZoomContext);
  const onCreateNode = () => {
    void createNode(nodeType, getCenterPosition());
  };
  return (
    <Box
      pb={2}
      pt={2}
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      gap={2}
      data-testid="createNodeButton"
      onClick={onCreateNode}
    >
      <Box height="16px" width="16px" borderRadius="2px" bgcolor={categoryColor} />
      <Typography data-cy="Channel-Node-Tree-View-Channel-Node-Type-View" variant="body2">
        {alias || name}
      </Typography>
      {arrow && (
        <Box
          sx={{
            width: 0,
            height: 0,
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            borderRight: `12px solid ${palette.grey[900]}`,
            position: 'absolute',
            right: '-8px',
          }}
        />
      )}
    </Box>
  );
};

const ChannelNodeTooltip = ({ node }: { node: ChannelNode }) => {
  const { palette } = useTheme();
  const tooltip = node?.tooltip.replaceAll('\\n', '\n');
  return (
    <Stack
      gap={1}
      width="256px"
      sx={{
        border: `8px solid ${palette.grey[900]}`,
        backgroundColor: palette.grey[900],
      }}
    >
      <Typography variant="caption2" color={palette.grey[100]} sx={{ whiteSpace: 'pre-line' }}>
        {tooltip}
      </Typography>
      {node?.thumbnail && <img src={node?.thumbnail} alt={node?.name} />}
    </Stack>
  );
};

const ChannelNodeTypeView = ({
  nodeType,
  categoryColor,
}: {
  nodeType: ChannelNode;
  categoryColor: string;
}) => {
  const { category, subcategory, name } = nodeType;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  return (
    <>
      <TreeItem
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
        nodeId={`${category || 'category'}.${subcategory || 'subcategory'}.${name}`}
        label={ChannelNodeTypeViewLabel({ nodeType, categoryColor, arrow: Boolean(anchorEl) })}
        onMouseEnter={(event) => setAnchorEl(event.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
      />
      <Popover
        open={Boolean(anchorEl) && (Boolean(nodeType?.tooltip) || Boolean(nodeType?.thumbnail))}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disablePortal
        style={{ pointerEvents: 'none' }}
        hideBackdrop
        sx={{
          '& .MuiPopover-paper': {
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        }}
      >
        <ChannelNodeTooltip node={nodeType} />
      </Popover>
    </>
  );
};

const ChannelSubCatalogView = ({
  data,
  category,
  subcategory,
}: {
  data: ChannelNode[];
  category: string;
  subcategory: string;
}) => {
  const { palette } = useTheme();
  const nodeList =
    category === undefined || category === null
      ? data.filter(
          ({ category: nodeCategory }) => nodeCategory === undefined || nodeCategory === null,
        )
      : data.filter(
          ({ category: nodeCategory, subcategory: nodeSubcategory }) =>
            category === nodeCategory && subcategory === nodeSubcategory,
        );

  nodeList.sort((a, b) => a.name.localeCompare(b.name));
  const [categoryColor] = data.filter((n) => n.category === category).map((n) => n.color);

  return (
    <TreeItem
      sx={{
        '&:hover': {
          backgroundColor: 'transparent',
        },
        marginLeft: 8,
        '& .MuiTreeItem-group': {
          marginLeft: 0,
        },
      }}
      nodeId={
        category !== undefined &&
        category !== null &&
        subcategory !== undefined &&
        subcategory !== null
          ? `${category}.${subcategory}`
          : Math.random().toString()
      }
      label={
        <Box pt={2} pb={2} color="grey.700">
          <Typography variant="body2" color={palette.text.paper}>
            {subcategory === undefined || subcategory === null ? 'Undefined' : subcategory}
          </Typography>
        </Box>
      }
    >
      {nodeList.map((node) => {
        return (
          <ChannelNodeTypeView
            key={`${category}|${subcategory}|${node.name}`}
            nodeType={node}
            categoryColor={categoryColor || 'secondary.main'}
          />
        );
      })}
    </TreeItem>
  );
};

const ChannelCatalogView = ({ category, data }: { category: string; data: ChannelNode[] }) => {
  const matchingNodes = data.filter((n) => n.category === category);
  const subCategoryList = Array.from(new Set(matchingNodes.map((n) => n.subcategory)));
  subCategoryList.sort();
  const [categoryColor] = matchingNodes.map((n) => n.color);

  return (
    <TreeItem
      nodeId={
        category === undefined || category === null
          ? 'Undefined'
          : category || Math.random().toString()
      }
      label={
        <Box py={2} display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
          <Box
            height="16px"
            width="16px"
            borderRadius="2px"
            bgcolor={categoryColor || 'secondary.main'}
          />
          <Typography data-cy="Channel-Node-Tree-View-Channel-Catalog-View" variant="body1">
            {category === undefined || category === null ? 'Undefined' : category}
          </Typography>
        </Box>
      }
      sx={{ py: 2 }}
    >
      {(subCategoryList.length > 0 ? subCategoryList : ['Undefined']).map((subCategory) => (
        <ChannelSubCatalogView
          data={data}
          key={subCategory}
          category={category}
          subcategory={subCategory}
        />
      ))}
    </TreeItem>
  );
};

export const ChannelNodeTreeView = () => {
  const { organizationId } = useOrganizationContext();
  const { channelNodes: data } = useContext(ChannelDetailContext);
  const { graphMetadata } = useContext(GraphDataContext);
  const { data: categoryData, onSearch } = useSearch(data, ['category', 'name', 'subcategory']);
  const categoryList = Array.from(
    new Set(((categoryData as ChannelNode[]) ?? []).map((n) => n.category)),
  );
  categoryList.sort();
  const handleSearchChange = ({ target: { value } }: { target: { value: string } }) => {
    onSearch(value);
  };
  return (
    <Stack gap={4} pt={4}>
      <Group alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{graphMetadata?.channel || ''}</Typography>
        <ExternalLink
          unstyled
          href={`/${organizationId}/channels/${graphMetadata?.channelId}/documentation`}
        >
          <Group>
            <DocumentationIcon size={16} />
            <ForwardIcon size={16} />
          </Group>
        </ExternalLink>
      </Group>
      {(data?.length ?? 0) > 0 && (
        <TextInput
          data-cy="Channel-Node-Tree-View-Search"
          data-testid="search"
          name="search"
          startIcon={SearchIcon}
          onChange={handleSearchChange}
        />
      )}
      <TreeView>
        {categoryList.length === 0 && (
          <Box display="flex" justifyContent="center">
            <Typography variant="body1">There is no node to choose from.</Typography>
          </Box>
        )}
        {categoryList.length > 0 &&
          categoryList.map((category) => (
            <ChannelCatalogView
              category={category}
              data={categoryData as ChannelNode[]}
              key={category}
            />
          ))}
      </TreeView>
    </Stack>
  );
};
